import { useEffect, useState } from 'react';
import { syncSentryUser } from '@/lib/sentry-user';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchCurrentUser, setCurrentPerson, setCurrentUser } from '@/db/services/user_service';
import { refreshPrograms, refreshSites } from '@/db/services/program_site_service';
import PersonSelector from '../components/PersonSelector';
import AuthLayout from '../components/AuthLayout';
import { Button } from '@mes-amis/galette-design-system';
import { Person } from '@/db/types/user';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

function refreshProgramsAndSites() {
  Promise.all([refreshPrograms(), refreshSites()]).catch(err => {
    console.error('Failed to refresh programs/sites:', err);
  });
}

export default function SelectOrganization() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        const userData = await fetchCurrentUser();

        // Store full user data for access to people array
        await setCurrentUser(userData);

        if (!userData.people || userData.people.length === 0) {
          setError('No organizations available for your account. Please contact your administrator.');
          return;
        }

        setPeople(userData.people);

        // If only one organization, auto-select it
        if (userData.people.length === 1) {
          const person = userData.people[0];
          await setCurrentPerson(person);
          await syncSentryUser();

          // Fetch programs and sites for the selected person (non-blocking)
          Promise.all([refreshPrograms(), refreshSites()]).catch(err => {
            console.error('Failed to refresh programs/sites:', err);
          });

          // Redirect to return_to or home
          const returnTo = searchParams.get('return_to');
          if (returnTo) {
            navigate(decodeURIComponent(returnTo));
          } else {
            navigate('/');
          }
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load organizations. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [navigate, searchParams]);

  const handlePersonSelected = async (person: Person) => {
    try {
      setProcessing(true);

      // Refresh user data before switching person so downstream consumers
      // (e.g. Sidebar) read up-to-date user metadata for the new org.
      const userData = await fetchCurrentUser();
      await setCurrentUser(userData);
      await setCurrentPerson(person);
      await syncSentryUser();

      // Fetch programs and sites for the selected person (non-blocking)
      refreshProgramsAndSites();

      // Redirect to return_to or home
      const returnTo = searchParams.get('return_to');
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to select organization:', err);
      setError('Failed to select organization. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-deep-sea-green"></div>
          <h1 className="font-heading text-[28px] leading-8 text-black">
            Loading Organizations
          </h1>
          <p className="text-sm leading-5 text-[#555]">
            Please wait while we load your available organizations...
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (error) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400" />
          <h1 className="font-heading text-[28px] leading-8 text-black">
            Something went wrong
          </h1>
          <p className="text-sm leading-5 text-[#555]">{error}</p>
        </div>
        <div className="flex flex-col items-start gap-2">
          <Button variant="primary" onClick={() => navigate('/login')} size="md">
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <PersonSelector
      people={people}
      onPersonSelected={handlePersonSelected}
      isLoading={processing}
    />
  );
}
